import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import openai from '../../../services/openaiClient';
import { Banknote, Sparkles, TrendingUp, PieChart, Calculator, FileSpreadsheet } from 'lucide-react';



const RevenueActionsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('analysis');
  const [loading, setLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [targetRevenue, setTargetRevenue] = useState('');

  const analyzeRevenue = async (analysisType) => {
    setLoading(true);
    try {
      const prompts = {
        trends: 'قم بتحليل اتجاهات الإيرادات الحالية وحدد العوامل المؤثرة والفرص المتاحة',
        forecast: 'قدم توقعات الإيرادات للأشهر الستة القادمة بناءً على البيانات التاريخية والاتجاهات',
        optimization: 'قم بتحليل مصادر الإيرادات وقدم توصيات لتحسين وزيادة الإيرادات',
        breakdown: 'قم بتحليل تفصيلي لمصادر الإيرادات وحدد المصادر الأكثر ربحية',
        target: `قم بتحليل إمكانية تحقيق هدف إيرادات بقيمة ${targetRevenue} وقدم خطة عمل`
      };

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: 'أنت محلل مالي متخصص في تحليل الإيرادات وتقديم استراتيجيات لتحسين الأداء المالي.'
          },
          {
            role: 'user',
            content: prompts?.[analysisType]
          }
        ],
        max_tokens: 500,
        temperature: 0.7
      });

      setAiAnalysis(response?.choices?.[0]?.message?.content);
    } catch (error) {
      console.error('Error analyzing revenue:', error);
      setAiAnalysis('حدث خطأ في التحليل. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'analysis', label: 'تحليل مالي', icon: Sparkles },
    { id: 'breakdown', label: 'التفصيل', icon: PieChart },
    { id: 'reports', label: 'التقارير المالية', icon: FileSpreadsheet }
  ];

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="تقارير الإيرادات - تحليلات مالية" 
      size="large"
      footer={null}
      icon={Banknote}
      iconColor="text-primary"
    >
      <div className="flex border-b border-border mb-6">
        {tabs?.map((tab) => {
          const TabIcon = tab?.icon;
          return (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <TabIcon className="w-4 h-4" />
              {tab?.label}
            </button>
          );
        })}
      </div>
      {activeTab === 'analysis' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              onClick={() => analyzeRevenue('trends')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <TrendingUp className="w-6 h-6" />
              <span>تحليل اتجاهات الإيرادات</span>
            </Button>
            <Button
              onClick={() => analyzeRevenue('forecast')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <Calculator className="w-6 h-6" />
              <span>توقعات الإيرادات</span>
            </Button>
            <Button
              onClick={() => analyzeRevenue('optimization')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <Sparkles className="w-6 h-6" />
              <span>تحسين الإيرادات</span>
            </Button>
            <Button
              onClick={() => analyzeRevenue('breakdown')}
              disabled={loading}
              className="h-24 flex flex-col items-center justify-center gap-2"
            >
              <PieChart className="w-6 h-6" />
              <span>تحليل مصادر الإيرادات</span>
            </Button>
          </div>

          <div className="flex gap-2">
            <Input
              placeholder="هدف الإيرادات (مثال: 500000)"
              value={targetRevenue}
              onChange={(e) => setTargetRevenue(e?.target?.value)}
              type="number"
              className="flex-1"
            />
            <Button
              onClick={() => analyzeRevenue('target')}
              disabled={loading || !targetRevenue}
            >
              <Banknote className="w-4 h-4 ml-2" />
              تحليل الهدف
            </Button>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="mr-3 text-muted-foreground">جاري التحليل المالي...</span>
            </div>
          )}

          {aiAnalysis && !loading && (
            <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-destructive" />
                <h3 className="font-semibold text-foreground">تحليل مالي ذكي</h3>
              </div>
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{aiAnalysis}</p>
            </div>
          )}
        </div>
      )}
      {activeTab === 'breakdown' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { source: 'إيجارات الشقق', amount: '١٤٥,٠٠٠ د.إ', percentage: '٤٢٪', color: 'bg-blue-500' },
              { source: 'إيجارات الفلل', amount: '٩٨,٠٠٠ د.إ', percentage: '٢٨٪', color: 'bg-green-500' },
              { source: 'إيجارات المكاتب', amount: '٦٧,٠٠٠ د.إ', percentage: '٢٠٪', color: 'bg-yellow-500' },
              { source: 'إيجارات المحلات', amount: '٣٥,٠٠٠ د.إ', percentage: '١٠٪', color: 'bg-purple-500' }
            ]?.map((item, idx) => (
              <div key={idx} className="border border-border rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-3 h-3 rounded-full ${item?.color}`}></div>
                  <h4 className="font-medium text-foreground">{item?.source}</h4>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">المبلغ</span>
                  <span className="text-lg font-bold text-foreground">{item?.amount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">النسبة</span>
                  <span className="text-sm font-medium text-foreground">{item?.percentage}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {activeTab === 'reports' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: 'تقرير الإيرادات الشهري', desc: 'ملخص الإيرادات للشهر الحالي' },
              { title: 'تقرير المقارنة السنوية', desc: 'مقارنة الإيرادات مع العام السابق' },
              { title: 'تقرير التدفق النقدي', desc: 'تحليل التدفقات النقدية الداخلة والخارجة' },
              { title: 'تقرير الربحية', desc: 'تحليل هوامش الربح والربحية' }
            ]?.map((report, idx) => (
              <div key={idx} className="border border-border rounded-lg p-4 hover:border-destructive transition-colors cursor-pointer">
                <h4 className="font-medium text-foreground mb-1">{report?.title}</h4>
                <p className="text-xs text-muted-foreground mb-3">{report?.desc}</p>
                <Button size="sm" variant="outline">إنشاء التقرير</Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default RevenueActionsModal;